import { useEffect, useState } from 'react';
import {
  Activity, Cpu, ArrowDown, ArrowUp, RefreshCw, Info,
} from 'lucide-react';
import Alert from './Alert';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { useDarkMode } from '../../context/DarkModeContext';
import { api } from '../../lib/api';
import { fmtTokens, type AdminUser } from './UsersTab';

type Range = '24h' | '7d' | '30d';

const RANGE_TO_HOURS: Record<Range, number> = {
  '24h': 24,
  '7d': 168,
  '30d': 720,
};

const RANGE_LABEL: Record<Range, string> = {
  '24h': '24 Stunden',
  '7d': '7 Tage',
  '30d': '30 Tage',
};

// Stand 2026 — vor Implementierung gegen anthropic.com/pricing pruefen
const MODEL_COST_PER_MILLION_TOKENS: Record<string, { in: number; out: number }> = {
  sonnet: { in: 3.00, out: 15.00 },
  opus:   { in: 15.00, out: 75.00 },
  haiku:  { in: 0.80, out: 4.00 },
};

interface UsageResponse {
  rangeHours: number;
  totals: { calls: number; inputTokens: number; outputTokens: number };
  perModel: Array<{ model: string; calls: number; inputTokens: number; outputTokens: number }>;
  perUser: Array<{ userId: number | null; calls: number; inputTokens: number; outputTokens: number }>;
  estimated: boolean;
}

interface UsageTabProps {
  users: AdminUser[];
}

const DONUT_COLORS = ['#3b82f6', '#a855f7', '#f59e0b', '#10b981', '#ef4444'];

function estimateCostUSD(model: string, inputTokens: number, outputTokens: number): number {
  const rate = MODEL_COST_PER_MILLION_TOKENS[model.toLowerCase()];
  if (!rate) return 0;
  return (inputTokens / 1_000_000) * rate.in + (outputTokens / 1_000_000) * rate.out;
}

function formatUSD(n: number): string {
  return `$${n.toFixed(2)}`;
}

export default function UsageTab({ users }: UsageTabProps) {
  const { darkMode } = useDarkMode();
  const [range, setRange] = useState<Range>('24h');
  const [data, setData] = useState<UsageResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.get<UsageResponse>(`/api/prompts/admin/usage?hours=${RANGE_TO_HOURS[range]}`);
      setData(res);
    } catch {
      setError('Daten konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range]);

  const userLookup = (uid: number | null) => {
    if (uid === null) return { label: '— (anonym)', muted: true };
    const u = users.find(x => x.id === uid);
    if (u) return { label: `${u.companyName || u.contactPerson} (${u.email})`, muted: false };
    return { label: `User #${uid} (nicht in Liste)`, muted: true };
  };

  const card = darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200';
  const cardClass = `rounded-xl border ${card}`;
  const muted = darkMode ? 'text-slate-400' : 'text-slate-500';

  const totalCostUSD = (data?.perModel ?? []).reduce(
    (sum, r) => sum + estimateCostUSD(r.model, r.inputTokens, r.outputTokens),
    0,
  );
  const costPrefix = data?.estimated ? '~' : '';

  return (
    <div className="space-y-6">
      {/* Zeitraum-Selector + Refresh + Estimated-Badge */}
      <div className="flex flex-wrap items-center gap-3">
        <div className={`inline-flex rounded-lg border overflow-hidden ${darkMode ? 'border-slate-700' : 'border-slate-300'}`}>
          {(['24h', '7d', '30d'] as Range[]).map(r => (
            <button
              key={r}
              onClick={() => setRange(r)}
              className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                range === r
                  ? 'bg-blue-600 text-white'
                  : darkMode ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              {RANGE_LABEL[r]}
            </button>
          ))}
        </div>

        <button
          onClick={load}
          disabled={loading}
          className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border transition-colors disabled:opacity-50 ${
            darkMode ? 'border-slate-700 text-slate-300 hover:bg-slate-700' : 'border-slate-300 text-slate-600 hover:bg-slate-50'
          }`}
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          Aktualisieren
        </button>

        {data?.estimated && (
          <span
            className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700"
            title="Token-Zahlen sind Schaetzwerte (Zeichenlaenge ÷ 4), bis SDK-Migration #1 echte Werte liefert."
          >
            <Info className="w-3 h-3" />
            Geschätzt
          </span>
        )}
      </div>

      {error && (
        <Alert type="error">
          {error}
          <button onClick={load} className="ml-2 text-xs underline">Neu versuchen</button>
        </Alert>
      )}

      {/* KPI-Kacheln */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Kpi icon={Activity}     label="Calls"        value={String(data?.totals.calls ?? 0)} cardClass={cardClass} muted={muted} />
        <Kpi icon={ArrowDown}    label="Input-Tokens" value={fmtTokens(data?.totals.inputTokens ?? 0)} cardClass={cardClass} muted={muted} />
        <Kpi icon={ArrowUp}      label="Output-Tokens" value={fmtTokens(data?.totals.outputTokens ?? 0)} cardClass={cardClass} muted={muted} />
        <Kpi icon={Cpu}          label="Geschätzte Kosten" value={`${costPrefix}${formatUSD(totalCostUSD)}`} cardClass={cardClass} muted={muted} />
      </div>

      {/* Donut */}
      <div className={`${cardClass} p-4`}>
        <h3 className={`text-sm font-semibold mb-3 ${darkMode ? 'text-white' : 'text-slate-900'}`}>
          Verteilung nach Modell ({data?.totals.calls ?? 0} Calls)
        </h3>
        {(data?.perModel?.length ?? 0) === 0 ? (
          <p className={`text-sm py-8 text-center ${muted}`}>Keine Daten im Zeitraum</p>
        ) : (
          <div style={{ width: '100%', height: 240 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  data={data?.perModel ?? []}
                  dataKey="calls"
                  nameKey="model"
                  innerRadius={50}
                  outerRadius={90}
                  paddingAngle={2}
                >
                  {(data?.perModel ?? []).map((_, i) => (
                    <Cell key={i} fill={DONUT_COLORS[i % DONUT_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Tabelle pro Modell */}
      <div className={`${cardClass} overflow-x-auto`}>
        <h3 className={`text-sm font-semibold p-4 ${darkMode ? 'text-white' : 'text-slate-900'}`}>Pro Modell</h3>
        <table className="w-full min-w-[560px] text-sm">
          <thead>
            <tr className={`border-y ${darkMode ? 'border-slate-700 bg-slate-700/30' : 'border-slate-200 bg-slate-50'}`}>
              <th className="px-4 py-2 text-left">Modell</th>
              <th className="px-4 py-2 text-right">Calls</th>
              <th className="px-4 py-2 text-right">Input</th>
              <th className="px-4 py-2 text-right">Output</th>
              <th className="px-4 py-2 text-right">~Kosten</th>
            </tr>
          </thead>
          <tbody className={`divide-y ${darkMode ? 'divide-slate-700' : 'divide-slate-200'}`}>
            {(data?.perModel ?? []).map(r => (
              <tr key={r.model}>
                <td className="px-4 py-2 font-medium">{r.model}</td>
                <td className="px-4 py-2 text-right">{r.calls}</td>
                <td className="px-4 py-2 text-right">{fmtTokens(r.inputTokens)}</td>
                <td className="px-4 py-2 text-right">{fmtTokens(r.outputTokens)}</td>
                <td className="px-4 py-2 text-right">{costPrefix}{formatUSD(estimateCostUSD(r.model, r.inputTokens, r.outputTokens))}</td>
              </tr>
            ))}
            {(data?.perModel?.length ?? 0) === 0 && (
              <tr><td colSpan={5} className={`px-4 py-6 text-center ${muted}`}>Keine Daten</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Tabelle pro Benutzer */}
      <div className={`${cardClass} overflow-x-auto`}>
        <h3 className={`text-sm font-semibold p-4 ${darkMode ? 'text-white' : 'text-slate-900'}`}>
          Pro Benutzer (Top {Math.min(100, data?.perUser?.length ?? 0)})
        </h3>
        <table className="w-full min-w-[560px] text-sm">
          <thead>
            <tr className={`border-y ${darkMode ? 'border-slate-700 bg-slate-700/30' : 'border-slate-200 bg-slate-50'}`}>
              <th className="px-4 py-2 text-left">Benutzer</th>
              <th className="px-4 py-2 text-right">Calls</th>
              <th className="px-4 py-2 text-right">Input</th>
              <th className="px-4 py-2 text-right">Output</th>
            </tr>
          </thead>
          <tbody className={`divide-y ${darkMode ? 'divide-slate-700' : 'divide-slate-200'}`}>
            {(data?.perUser ?? []).map(r => {
              const lookup = userLookup(r.userId);
              return (
                <tr key={`u-${r.userId ?? 'anon'}`}>
                  <td className={`px-4 py-2 ${lookup.muted ? muted : ''}`}>{lookup.label}</td>
                  <td className="px-4 py-2 text-right">{r.calls}</td>
                  <td className="px-4 py-2 text-right">{fmtTokens(r.inputTokens)}</td>
                  <td className="px-4 py-2 text-right">{fmtTokens(r.outputTokens)}</td>
                </tr>
              );
            })}
            {(data?.perUser?.length ?? 0) === 0 && (
              <tr><td colSpan={4} className={`px-4 py-6 text-center ${muted}`}>Keine Daten</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, cardClass, muted }: {
  icon: typeof Activity;
  label: string;
  value: string;
  cardClass: string;
  muted: string;
}) {
  return (
    <div className={`${cardClass} p-4`}>
      <div className={`flex items-center gap-1.5 text-[12px] uppercase tracking-wide ${muted}`}>
        <Icon className="w-3 h-3" />{label}
      </div>
      <p className="text-2xl font-bold mt-1">{value}</p>
    </div>
  );
}
